import type { AnyRatingService } from '@/stores/ratingService';

export type ServiceHash = string;
type Domain = string;

export type ImageMetadata = {
  services: ServiceDict;
  metadata: Metadata[];
};

export type ServiceDict = {
  [key: ServiceHash]: AnyRatingService;
};

export type Metadata = {
  file_id: number;
  hash: string;
  size?: number;
  mime: string;
  ext: string;
  width?: number;
  height?: number;
  /** Base-83 encoded blurhash string (only present for files that have a thumbnail). */
  blurhash?: string;
  /** SHA-256 of the image's pixel data. */
  pixel_hash?: string;
  duration?: number;
  num_frames?: number;
  num_words?: number;
  has_audio?: boolean;
  thumbnail_width: number;
  thumbnail_height: number;
  time_modified?: number;
  time_modified_details: TimeModifiedDetails | {};
  file_services: FileServices;
  ipfs_multihashes: IpfsMultihashes;
  is_inbox: boolean;
  is_local: boolean;
  is_trashed: boolean;
  is_deleted: boolean;
  has_exif: boolean;
  has_human_readable_embedded_metadata: boolean;
  has_icc_profile: boolean;
  known_urls: string[];
  ratings: Ratings;
  tags: TagServices;
};

export type TimeModifiedDetails = {
  local: number;
  [key: Domain]: number;
};

export type FileServices = {
  current: Current;
  deleted: Deleted;
};

export type Current = {
  [key: ServiceHash]: ImportTime;
};

export type ImportTime = {
  time_imported: number;
};

export type Deleted = {
  [key: ServiceHash]: DeleteTime;
};

export type DeleteTime = ImportTime & {
  time_deleted: number;
};

export type IpfsMultihashes = {
  [key: string]: string;
};

export type Ratings = {
  [key: ServiceHash]: boolean | number | null;
};

/**
 * client api/57 (555)
 */
export type TagServices = {
  [key: ServiceHash]: TagService;
};

/**
 * client api/57 (555)
 */
export type TagService = {
  name: string;
  type: number;
  type_pretty: string;

  /**
   * While the 'storage_tags' represent the actual tags stored on the database for a file,
   * 'display_tags' reflect how tags appear in the UI, after siblings are collapsed and parents are added.
   * If you want to edit a file's tags, refer to the storage tags.
   * If you want to render to the user, use the display tags.
   * The display tag calculation logic is very complicated;
   * if the storage tags change, do not try to guess the new display tags yourself--just ask the API again.
   **/
  storage_tags: Tags;
  /**
   * While the 'storage_tags' represent the actual tags stored on the database for a file,
   * 'display_tags' reflect how tags appear in the UI, after siblings are collapsed and parents are added.
   * If you want to edit a file's tags, refer to the storage tags.
   * If you want to render to the user, use the display tags.
   * The display tag calculation logic is very complicated;
   * if the storage tags change, do not try to guess the new display tags yourself--just ask the API again.
   **/
  display_tags: Tags;
};

/**
 * client api/57 (555)
 * The tags structure is similar to the /add_tags/add_tags scheme, excepting that the status numbers are:
 * 0 - current
 * 1 - pending
 * 2 - deleted
 * 3 - petitioned
 **/
export enum TagState {
  CURRENT = '0',
  PENDING = '1',
  DELETED = '2',
  PETITIONED = '3',
}

/**
 * client api/57 (555)
 * The tags structure is similar to the /add_tags/add_tags scheme, excepting that the status numbers are:
 * 0 - current
 * 1 - pending
 * 2 - deleted
 * 3 - petitioned
 **/
export type Tags = {
  [TagState.CURRENT]?: string[];
  [TagState.PENDING]?: string[];
  [TagState.DELETED]?: string[];
  [TagState.PETITIONED]?: string[];
};

/**
 * The permitted 'actions' are:
 *
 * 0 - Add to a local tag service.
 * 1 - Delete from a local tag service.
 * 2 - Pend to a tag repository.
 * 3 - Rescind a pend from a tag repository.
 * 4 - Petition from a tag repository. (This is special)
 * 5 - Rescind a petition from a tag repository.
 */
export enum TagApiAction {
  ADD = '0',
  DELETE = '1',
  PEND = '2',
  RESCIND_PEND = '3',
  PETITION = '4',
  RESCIND_PETITION = '5',
}

export type SearchTagResultTag = {
  value: string;
  count: number;
};

export type SearchTagResult = {
  tags: SearchTagResultTag[];
};
